import { QueryClient } from 'react-query';
import { Updater } from 'react-query/types/core/utils';
import axios from 'axios';
import { enqueueSnackbar, openErrorBoundary } from '../design-system';
import { authService, userVisitsService } from '../services';
import { getDeviceId } from '../utils';
import { i18n } from '../i18n';
import { MESSAGES } from '../const';
import { clearSavedFilters } from '../hooks';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
    },
  },
});

type RemoveCacheProps =
  | (
      | 'container'
      | 'gate-ins'
      | 'gate-outs'
      | 'repair'
      | 'inventory'
      | 'invoices'
      | 'user-visits'
      | 'sale'
      | 'sales-fee'
      | 'offer'
      | 'yard-companies'
      | 'yard'
      | 'client'
      | 'employee'
      | 'chat'
      | string
    )[]
  | '*'
  | undefined;

export const removeCache = async (key: RemoveCacheProps) => {
  if (key === '*' || !key) {
    return queryClient.clear();
  }

  key.forEach((queryKey) => {
    queryClient.removeQueries([queryKey]);

    if (queryKey !== 'gate-ins' && queryKey !== 'gate-outs' && queryKey !== 'employee')
      queryClient.removeQueries([queryKey + '-short']);

    if (
      queryKey !== 'gate-ins' &&
      queryKey !== 'gate-outs' &&
      queryKey !== 'yard-companies' &&
      queryKey !== 'chat'
    )
      queryClient.removeQueries([queryKey + 's']);
  });
};

export const saveCache = <T>(key: string, data: Updater<T, T>) => {
  return queryClient.setQueryData<T>(key, data as Updater<T | undefined, T>);
};

export const getCache = <T>(key: string | string[]) => {
  return queryClient.getQueryData(key) as T;
};

// AXIOS INTERCEPTORS
let pending = 0;
const backdropWaitingQueue: Array<() => void> = [];
axios.defaults.baseURL = process.env.REACT_APP_TEZPORT_API_URL;

const disabledTokensForRequests = [
  '/auth/get-password-reset-instruction',
  '/auth/create-new-password',
  '/client-yards/yards',
  '/sales/client',
  '/sales/yard',
  '/mailer/client-interest-buy-container',
  'storage.googleapis.com',
];

export const controlBackdropLoading = (value: number) => {
  pending += value;

  const backdropEl = document.querySelector('#backdrop-loading');
  const isLoading = backdropEl?.className.includes('loading');

  if (pending && !isLoading) {
    backdropEl?.classList.add('loading');
  }

  if (!pending && isLoading) {
    backdropEl?.classList.remove('loading');
  }

  if (!pending && backdropWaitingQueue.length) {
    const queue = backdropWaitingQueue.splice(0);
    queue.forEach((callback) => callback());
  }
};

export const backdropWaitingFc = (fc: () => void) => {
  if (pending === 0) {
    fc();
    return;
  }

  backdropWaitingQueue.push(fc);
};

let isCreatingVisit: Promise<void> | null = null;

export const backdropLoadingAutoCall = () => {
  axios.interceptors.request.use(
    async (config: any) => {
      if (!config.params || !config.params.notLoading) controlBackdropLoading(1);

      const withCredentials =
        disabledTokensForRequests.every((link) => config.url.indexOf(link) === -1) &&
        !config.headers.Authorization;
      config.withCredentials = withCredentials;
      if (withCredentials) {
        if (config.url !== '/user-visits' && config.method !== 'post') {
          const deviceId = getDeviceId();
          if (!deviceId) {
            if (!isCreatingVisit) {
              isCreatingVisit = userVisitsService.create().finally(() => {
                isCreatingVisit = null;
              });
            }
            await isCreatingVisit;
          }
        }
        config.headers['X-Page-Url'] =
          config.headers['X-Page-Url'] !== 'none' ? window.location.pathname : '';
      }

      if (config.params && typeof config.params.notLoading === 'boolean') {
        config.customData = { notLoading: config.params.notLoading };
        delete config.params.notLoading;
      }

      return config;
    },
    (error: any) => {
      return Promise.reject(error);
    },
  );

  axios.interceptors.response.use(
    (response: any) => {
      const customData = response.config.customData;
      if (!customData || !customData.notLoading) controlBackdropLoading(-1);

      return response;
    },
    (error) => {
      const customData = error.config?.customData;
      error.statusCode = error.response?.status;
      if (!customData || !customData.notLoading) controlBackdropLoading(-1);

      if (!error.response || !error.response.data || !error.response.data.message) {
        error.response = { data: { message: error.message } };
      }
      if (error.response.data.message.includes('Connection timed out')) {
        error.response.data.message = MESSAGES.ERROR_BOUNDARY.DEFAULT;
      }

      if (error.statusCode === 499) {
        alert(MESSAGES.NETWORK.WARNING.WIFI_ERROR);
        return Promise.reject(error);
      }

      if (error.statusCode === 401) {
        alert(MESSAGES.AUTH.WARNING.RETRY_LOGIN);
        authService.logoutOnError();
        return Promise.reject(error);
      }

      if (error.code === 'ERR_NETWORK') {
        if (error?.config?.url?.includes('auth/login')) {
          alert(i18n.t('messages.network.warning.tryAgain'));
          authService.logoutOnError();
        } else {
          enqueueSnackbar(MESSAGES.NETWORK.ERROR, 'error');
        }
      }
      if (error.code === 'ERR_BAD_RESPONSE') {
        enqueueSnackbar(MESSAGES.NETWORK.BAD_RESPONSE, 'error');
      }
      if (
        (error.statusCode === 400 && error.response.data.message === 'Validation Error Occured') ||
        (error.statusCode === 403 && error.response.data.message.includes('Forbidden'))
      ) {
        clearSavedFilters();
        openErrorBoundary();
      }

      if (error.statusCode === 404 && error.response.data.message === 'Account does not exist') {
        authService.logoutOnError();
      }
      if (error.statusCode === 409) {
        enqueueSnackbar(MESSAGES.NETWORK.ALREADY_EXISTS, 'error');
      }
      return Promise.reject(error);
    },
  );
};
